import {
  Button,
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from 'urql';
import invariant from 'tiny-invariant';
import { useParams } from 'react-router-dom';
import { Select } from 'chakra-react-select';
import { useTranslation } from 'react-i18next';
import { GetProjectDeepDocument } from '../../../gql/graphql';
import { useBreadcrumb } from '../../common/BreadcrumbProvider';

type MoveOrCopyItemsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (orderId: string, shouldCopy: boolean) => Promise<void>;
};

export default function MoveOrCopyItemsModal({
  isOpen,
  onClose,
  onSubmit,
}: MoveOrCopyItemsModalProps): React.ReactElement {
  type MoveItemFormValues = {
    orderId: string;
    shouldCopy: boolean;
  };

  let { orderId } = useParams();

  const { t } = useTranslation('table');

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    control,
  } = useForm<MoveItemFormValues>({
    defaultValues: {
      orderId: '',
      shouldCopy: false,
    },
  });

  const selectedOrderId = orderId;

  const { projectId } = useBreadcrumb();

  invariant(projectId, 'Project ID is required');

  const [{ data: projectData }] = useQuery({
    query: GetProjectDeepDocument,
    variables: { id: projectId },
  });

  const onSubmitInternal = handleSubmit(async ({ orderId, shouldCopy }) => {
    await onSubmit(orderId, shouldCopy);
    onClose();
    reset();
  });

  const options = projectData?.project?.orders.map((order) => ({
    label: order.name,
    value: order.id,
    isDisabled: order.id === orderId,
  }));

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('move-or-copy-items-modal.title')}</ModalHeader>
        <Divider orientation="horizontal" />
        <ModalBody>
          <form id="move-or-copy-item-form" onSubmit={onSubmitInternal}>
            <FormControl isRequired>
              <FormLabel>
                {t('move-or-copy-items-modal.destination-table')}
              </FormLabel>
              <Controller
                name="orderId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t(
                      'move-or-copy-items-modal.select-table-placeholder'
                    )}
                    value={
                      options &&
                      options.find((option) => option.value === field.value)
                    }
                    options={options}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption?.value)
                    }
                  />
                )}
              ></Controller>
            </FormControl>
          </form>
        </ModalBody>
        <Divider orientation="horizontal" />
        <ModalFooter gap={2}>
          <Button
            onClick={onSubmitInternal}
            isDisabled={!selectedOrderId || selectedOrderId === ''}
            isLoading={isSubmitting}
            type="submit"
            form="move-or-copy-item-form"
          >
            {t('move-or-copy-items-modal.copy-button')}
          </Button>
          <Button colorScheme="gray" onClick={onClose}>
            {t('move-or-copy-items-modal.cancel-button')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
